import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { CompanyKeyPerson, CompanyNews } from 'types';
import type {
  CompanyMissionStatementProps,
  CPSCoverPictureProps,
  CPSPictureProps,
  CPSStep,
  CPSVideoProps,
  TCompanyDetails,
  TCompanyDisplayFlags,
} from 'types/cps';
import { v4 as uuid } from 'uuid';

import {
  COMPANY_PROFILE_SETTINGS_STEPS,
  COMPANY_PROFILE_SETTINGS_STEPS_DATA,
} from 'utils/constants-v2';

type Step = typeof COMPANY_PROFILE_SETTINGS_STEPS[keyof typeof COMPANY_PROFILE_SETTINGS_STEPS];

const isStep = (key: string | Step): key is Step => {
  return !!COMPANY_PROFILE_SETTINGS_STEPS_DATA[key];
};

type Checkbox = {
  enabled: boolean;
  description: string;
  error?: boolean;
};

export type CultureCharacteristic = {
  id?: string;
  image?: string;
  title?: string;
  description?: string;
  subtitle?: string;
};

export type CultureInterview = {
  id?: string;
  image?: string;
  employeeName?: string;
  interviewDescription?: string;
  employeeJobTitle?: string;
  interviewTitle?: string;
};

export type CultureListData = CultureCharacteristic | CultureInterview;

export type InitialState = {
  resetCount: number;
  step: CPSStep;
  prevStep: string;
  revalidate: boolean;
  benefits: {
    benefitsQuestions: Record<string, { answer: string; error?: string }>;
    healthcareAndWellnessBenefits: Record<string, Checkbox>;
    retirementAndInsuranceBenefits: Record<string, Checkbox>;
    vacationBenefits: Record<string, Checkbox>;
    familyFriendlyBenefits: Record<string, Checkbox>;
    otherBenefits: Record<string, Checkbox>;
  };
  culture: {
    companyCharacteristics: CultureCharacteristic[];
    employeeInterviews: CultureInterview[];
    companyCulture: Record<string, Checkbox>;
    diversityCulture: Record<string, Checkbox>;
    professionalDevelopmentBenefitsCulture: Record<string, Checkbox>;
  };
  companyNews: CompanyNews[];
  details: TCompanyDetails;
  pageDisplay: TCompanyDisplayFlags;
  companyKeyPeopleNew: CompanyKeyPerson[];
  companyPictures: CPSPictureProps[];
  companyVideos: CPSVideoProps[];
  companyCoverImage: string;
  companyCoverImageOriginal: string;
  cpsCompanyDescription: string;
  companyMissionStatement: CompanyMissionStatementProps;
};

type StepKeys = keyof InitialState;

type QuestionSteps = 'culture' | 'benefits';

export const companyProfileSettingsInitialState: InitialState = {
  resetCount: 0,
  prevStep: '' as CPSStep,
  step: COMPANY_PROFILE_SETTINGS_STEPS.DETAILS,
  revalidate: false,
  benefits: {
    benefitsQuestions: {},
    healthcareAndWellnessBenefits: {},
    retirementAndInsuranceBenefits: {},
    vacationBenefits: {},
    familyFriendlyBenefits: {},
    otherBenefits: {},
  },
  culture: {
    companyCharacteristics: [],
    employeeInterviews: [],
    companyCulture: {},
    diversityCulture: {},
    professionalDevelopmentBenefitsCulture: {},
  },
  companyNews: [],
  details: {} as TCompanyDetails,
  pageDisplay: {} as TCompanyDisplayFlags,
  companyKeyPeopleNew: [],
  companyPictures: [],
  companyVideos: [],
  companyCoverImage: '',
  companyCoverImageOriginal: '',
  cpsCompanyDescription: '',
  companyMissionStatement: {},
};

const cpsSlice = createSlice({
  name: 'companyProfileSettings',
  initialState: companyProfileSettingsInitialState,
  reducers: {
    changeStep(state, action: PayloadAction<{ step: string; revalidate?: boolean }>) {
      const { step, revalidate = false } = action.payload;

      if (isStep(step)) {
        state.prevStep = state.step;
        state.step = step;
      }

      state.revalidate = revalidate;
    },
    nextStep(state) {
      const CPS_STEPS = Object.values(COMPANY_PROFILE_SETTINGS_STEPS);
      const currentStepIndex = CPS_STEPS.findIndex((step) => step === state.step);
      if (currentStepIndex >= 0 && currentStepIndex < CPS_STEPS.length - 1) {
        state.prevStep = state.step;
        state.step = CPS_STEPS[currentStepIndex + 1];
      }
    },
    resetChanges(state, action: PayloadAction<Partial<InitialState>>) {
      return {
        ...state,
        ...action.payload,
        resetCount: state.resetCount + 1,
      };
    },
    resetRevalidate(state) {
      state.revalidate = false;
    },
    toggleCheckbox(
      state,
      action: PayloadAction<{ step: StepKeys; key: string; item: string; value: boolean }>
    ) {
      const { step, key, item, value } = action.payload;

      if (state?.[step]?.[key]?.[item]) {
        state[step][key][item].enabled = value;
      } else {
        state[step][key] = {
          ...state[step][key],
          [item]: {
            enabled: value,
            description: '',
          },
        };
      }
    },
    updateCheckboxDescription(
      state,
      action: PayloadAction<{
        step: StepKeys;
        key: string;
        item: string;
        description: string;
      }>
    ) {
      const { step, key, item, description } = action.payload;

      state[step][key][item].description = description;
    },
    updateQuestionAnswer(
      state,
      action: PayloadAction<{
        step: QuestionSteps;
        key: string;
        question: string;
        answer: string;
      }>
    ) {
      const { step, key, question, answer } = action.payload;
      state[step][key][question] = { answer };
    },
    postNews(state, action: PayloadAction<CompanyNews>) {
      const id = uuid();
      state.companyNews = [
        {
          ...action.payload,
          id,
          submitTime: Date.now(),
        },
        ...state.companyNews,
      ];
    },
    updateNews(state, action: PayloadAction<{ key: number; data: CompanyNews }>) {
      state.companyNews[action.payload.key] = {
        ...state.companyNews[action.payload.key],
        ...action.payload.data,
        lastUpdateTime: Date.now(),
      };
    },
    deleteNews(state, action: PayloadAction<number>) {
      state.companyNews.splice(action.payload, 1);
    },
    /* Receives an update object and _merges_ it to state.details. */
    updateCompanyDetails(state, action: PayloadAction<Record<string, string | number>>) {
      state.details = { ...state.details, ...action.payload };
    },
    updatePageDisplay(state, action: PayloadAction<Record<string, boolean>>) {
      state.pageDisplay = { ...state.pageDisplay, ...action.payload };
    },
    /**
     * Creates a new CompanyKeyPerson and add it to the CompanyKeyPeople array
     */
    createCompanyKeyPerson(state, action: PayloadAction<CompanyKeyPerson>) {
      state.companyKeyPeopleNew = state.companyKeyPeopleNew.concat(action.payload);
    },
    /**
     * Updates an existing CompanyKeyPerson and update the same in CompanyKeyPeople array
     */
    updateCompanyKeyPerson(
      state,
      action: PayloadAction<{ index: number; keyPerson: CompanyKeyPerson }>
    ) {
      const companyKeyPeople = [...state.companyKeyPeopleNew];
      companyKeyPeople[action.payload.index] = action.payload.keyPerson;
      state.companyKeyPeopleNew = companyKeyPeople;
    },
    /**
     * Removes an existing CompanyKeyPerson from CompanyKeyPeople array
     */
    deleteCompanyKeyPerson(state, action: PayloadAction<{ index: number }>) {
      const companyKeyPeople = [...state.companyKeyPeopleNew];
      companyKeyPeople.splice(action.payload.index, 1);
      state.companyKeyPeopleNew = companyKeyPeople;
    },
    /**
     * Reorders CompanyKeyPersons in CompanyKeyPeople array using their source and destination indexes
     */
    reorderCompanyKeyPeople(
      state,
      action: PayloadAction<{ sourceIndex: number; destinationIndex: number }>
    ) {
      const { sourceIndex, destinationIndex } = action.payload;

      const companyKeyPeopleNew = [...state.companyKeyPeopleNew];

      // copy source to destination
      companyKeyPeopleNew[destinationIndex] = companyKeyPeopleNew[sourceIndex];

      if (sourceIndex > destinationIndex) {
        let i = sourceIndex;
        while (i > destinationIndex) {
          if (i == destinationIndex + 1) {
            companyKeyPeopleNew[i] = state.companyKeyPeopleNew[destinationIndex];
          } else {
            companyKeyPeopleNew[i] = companyKeyPeopleNew[i - 1];
          }
          i--;
        }
      } else {
        let i = sourceIndex;
        while (i < destinationIndex) {
          if (i == destinationIndex - 1) {
            companyKeyPeopleNew[i] = state.companyKeyPeopleNew[destinationIndex];
          } else {
            companyKeyPeopleNew[i] = companyKeyPeopleNew[i + 1];
          }
          i++;
        }
      }
      state.companyKeyPeopleNew = companyKeyPeopleNew;
    },
    updateCompanyPictures(state, action: PayloadAction<CPSPictureProps[]>) {
      state.companyPictures = action.payload;
    },
    updateCompanyVideos(state, action: PayloadAction<CPSVideoProps[]>) {
      state.companyVideos = action.payload;
    },
    updateCompanyCoverPicture(state, action: PayloadAction<CPSCoverPictureProps>) {
      state.companyCoverImage = action.payload.cropped;
      state.companyCoverImageOriginal = action.payload.original;
    },
    updateCompanyDescription(state, action: PayloadAction<string>) {
      state.cpsCompanyDescription = action.payload;
    },
    updateCompanyMissionStatement(state, action: PayloadAction<{ type: string; value: string }>) {
      const { type, value } = action.payload;

      state.companyMissionStatement[type] = value;
    },
    postCultureListSection(
      state,
      action: PayloadAction<{ section: string; data: CultureCharacteristic }>
    ) {
      state.culture[action.payload.section] = [
        { id: uuid(), ...action.payload.data },
        ...state.culture[action.payload.section],
      ];
    },
    updateCultureListSection(
      state,
      action: PayloadAction<{ section: string; key: number; data: CultureCharacteristic }>
    ) {
      const { section, key, data } = action.payload;

      state.culture[section][key] = data;
    },
    deleteCultureListSection(state, action: PayloadAction<{ section: string; key: number }>) {
      state.culture[action.payload.section].splice(action.payload.key, 1);
    },
    reorderCultureListSection(
      state,
      action: PayloadAction<{ section: string; source: number; destination: number }>
    ) {
      const { source, destination, section } = action.payload;

      const sectionData = state.culture[section];

      const newOrder = [...sectionData];
      newOrder[source] = sectionData[destination];
      newOrder[destination] = sectionData[source];

      state.culture[action.payload.section] = newOrder;
    },
  },
});

const { actions, reducer } = cpsSlice;

export const {
  nextStep,
  changeStep,
  resetChanges,
  resetRevalidate,
  toggleCheckbox,
  updateCheckboxDescription,
  updateQuestionAnswer,
  postNews,
  updateNews,
  deleteNews,
  updateCompanyDetails,
  updatePageDisplay,
  updateCompanyPictures,
  updateCompanyVideos,
  updateCompanyCoverPicture,
  updateCompanyDescription,
  updateCompanyMissionStatement,

  /* CompanyKeyPeople actions */
  createCompanyKeyPerson,
  updateCompanyKeyPerson,
  deleteCompanyKeyPerson,
  reorderCompanyKeyPeople,

  /* Company Culture actions */
  postCultureListSection,
  updateCultureListSection,
  deleteCultureListSection,
  reorderCultureListSection,
} = actions;

export default reducer;
